import { useCallback, useEffect, useRef, useState } from 'react'

import { DealNode, useAllDealsQuery } from '@/apollo/graphql'
import { getFetchMoreStatus } from '@/utils'
import { IPageInfo } from '@/types'

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = (customerId?: number) => {
  const prevKeyword = useRef<string>('')
  const [search, setSearch] = useState<string>('')
  const [deals, setDeals] = useState<Partial<DealNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)
  const [prevCustomerId, setPrevCustomerId] = useState(customerId)

  let currentOffset = pageInfo.offset || 0
  if (prevCustomerId !== customerId) {
    setPrevCustomerId(customerId)
    setDeals([])
    setPageInfo(initialPageInfo)
    currentOffset = 0
  }

  const { loading, networkStatus } = useAllDealsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      offset: currentOffset,
      first: pageSize,
      customer: customerId,
      text: search,
    },
    onCompleted: res => {
      if (res.allDeals?.edges) {
        const _deals: Partial<DealNode>[] = []
        res.allDeals.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) _deals.push(e.node)
        })

        setDeals(prev => (currentOffset === 0 ? _deals : [...prev, ..._deals]))
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: !!res.allDeals?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  const fetchMoreStatus = getFetchMoreStatus({
    networkStatus,
    hasNextPage: pageInfo?.hasNextPage,
    withForce: true,
  })

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: deals.length }))
  }, [deals?.length])

  const onSearch = useCallback((text: string) => {
    if (prevKeyword.current !== text) {
      setDeals([])
      prevKeyword.current = text
    }
    setSearch(text)
    setPageInfo(initialPageInfo)
  }, [])

  return {
    loading,
    onSearch,
    pageInfo: { ...pageInfo, fetchMoreStatus },
    data: deals,
    onFetchMore,
  }
}
